import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTable, usePagination } from 'react-table';
import { useDataContext } from 'common/lib/helpers/DataContext';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import { isDraft } from 'common/lib/helpers/adStatusHelpers';
import * as _ from 'lodash';
import NextIcon from '@fabric-ds/icons/dist/24/chevron-right.svg';
import PreviousIcon from '@fabric-ds/icons/dist/24/chevron-left.svg';
import * as S from './styles';
import { UNIT_FLOORPLAN, UNIT_APARTMENT, UNIT_FLOOR, UNIT_PRICE } from './constants';

const Table = ({ columns, data }) => {
    const hideFloorplanColumn = data.filter(unit => !_.isEmpty(unit[UNIT_FLOORPLAN.accessor])).length === 0;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            initialState: {
                hiddenColumns: [hideFloorplanColumn && UNIT_FLOORPLAN.accessor],
                pageSize: 15,
            },
            autoResetPage: false,
        },
        usePagination
    );

    const { layoutPath } = useDataContext();
    const [hoverBackground, setHoverbackground] = useState(null);
    const { isTablet, isMobile } = useDeviceSize();

    const isSmallScreen = isTablet || isMobile;

    const goToAd = finnkode => {
        window.open(`${layoutPath}/prosjekt?finnkode=${finnkode}`, '_blank');
    };

    return (
        <>
            <table {...getTableProps()} role="grid" className="w-full">
                <thead>
                    {headerGroups.map(headerGroup => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map(column => {
                                if (
                                    (column.id === UNIT_FLOOR.accessor || column.id === UNIT_FLOORPLAN.accessor) &&
                                    isSmallScreen
                                ) {
                                    return null;
                                }
                                return (
                                    <th
                                        {...column.getHeaderProps()}
                                        className={
                                            // eslint-disable-next-line no-nested-ternary
                                            column.id === UNIT_PRICE.accessor
                                                ? 'text-right'
                                                : column.id !== UNIT_APARTMENT.accessor
                                                ? 'text-center'
                                                : 'text-left'
                                        }
                                    >
                                        {column.render('Header')}
                                    </th>
                                );
                            })}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map(row => {
                        prepareRow(row);
                        return (
                            <S.TableBodyRow
                                {...row.getRowProps()}
                                isCurrentRow={hoverBackground === row}
                                useDraftBackgroundColor={isDraft(row.original.adStatus)}
                            >
                                {row.cells.map(cell => {
                                    if (
                                        (cell.column.id === UNIT_FLOOR.accessor ||
                                            cell.column.id === UNIT_FLOORPLAN.accessor) &&
                                        isSmallScreen
                                    ) {
                                        return null;
                                    }

                                    if (cell.column.id === UNIT_FLOORPLAN.accessor) {
                                        return (
                                            <td
                                                className="floor-plan-cell text-right"
                                                role="gridcell"
                                                tabIndex="-1"
                                                {...cell.getCellProps()}
                                                onMouseOver={() => setHoverbackground(null)}
                                                onFocus={() => {
                                                    setHoverbackground(null);
                                                }}
                                            >
                                                {cell.render('Cell')}
                                            </td>
                                        );
                                    }

                                    return (
                                        <S.Cell
                                            role="gridcell"
                                            priceCell={cell.column.id === UNIT_PRICE.accessor}
                                            centerCell={
                                                cell.column.id !== UNIT_PRICE.accessor &&
                                                cell.column.id !== UNIT_APARTMENT.accessor
                                            }
                                            className={
                                                cell.column.id === UNIT_APARTMENT.accessor && isSmallScreen
                                                    ? 'link link--block font-bold'
                                                    : ''
                                            }
                                            tabIndex="0"
                                            {...cell.getCellProps()}
                                            onClick={() => goToAd(row.original.finnkode)}
                                            onMouseEnter={() => setHoverbackground(row)}
                                            onMouseLeave={() => setHoverbackground(null)}
                                            onFocus={() => {
                                                setHoverbackground(row);
                                            }}
                                            onKeyPress={() => goToAd(row.original.finnkode)}
                                        >
                                            {cell.render('Cell')}
                                        </S.Cell>
                                    );
                                })}
                            </S.TableBodyRow>
                        );
                    })}
                </tbody>
            </table>

            {data.length > pageSize && (
                <nav className="flex items-center justify-center mt-8" aria-labelledby="Enhetsvelger">
                    <h1 className="sr-only" id="Enhetsvelger">
                        Liste av enheter i dette prosjektet
                    </h1>
                    {canPreviousPage && (
                        <button
                            aria-label="Forrige side"
                            type="button"
                            className="button button--pill"
                            onClick={() => previousPage()}
                        >
                            <PreviousIcon />
                        </button>
                    )}

                    <div>
                        {_.times(pageCount, i => (
                            <button
                                type="button"
                                className={`button button--pill text-blue-600 ${
                                    pageIndex === i && 'text-white bg-gray-700 hover:bg-gray-700'
                                }`}
                                onClick={() => gotoPage(i)}
                                aria-current={pageIndex === i && 'page'}
                            >
                                {i + 1}
                            </button>
                        ))}
                    </div>

                    {canNextPage && (
                        <button
                            aria-label="Neste side"
                            type="button"
                            className="button button--pill"
                            onClick={() => nextPage()}
                        >
                            <NextIcon />
                        </button>
                    )}
                </nav>
            )}
        </>
    );
};

Table.propTypes = {
    columns: PropTypes.array.isRequired,
    data: PropTypes.array.isRequired,
};

export default Table;
